import React, { useState, useEffect } from 'react';
import {
  Search, Filter, Download, Plus, ChevronDown, Briefcase,
  MapPin, Calendar, Edit, FileText, Trash2, AlertCircle,
  Star, Check, ChevronLeft, Settings
} from 'lucide-react';
import { useLanguage } from '../StoreContext/LanguageContext';
import apiService from '../config'; // Import your API service

const ProjectManagement = ({ onBack, setSubComponent }) => {
  const [view, setView] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filterAnimation, setFilterAnimation] = useState(false);
  const [error, setError] = useState(null);
  const { language, translations } = useLanguage();

  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertConfig, setAlertConfig] = useState({
    title: '',
    message: '',
    type: 'success'
  });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmConfig, setConfirmConfig] = useState({
    title: '',
    message: '',
    onConfirm: null,
    onCancel: null,
    type: 'warning'
  });

  const ROLES = {
    COLLECTOR: 1,
    LAO: 3,
    SAMANVAY: 6,
    PROJECT_INCHARGE: 18,
    DATA_ENTRY: 14
  };

  const [userRole, setUserRole] = useState(() => {
    try {
      const userData = localStorage.getItem("userData");
      if (userData) {
        const user = JSON.parse(userData);
        return user.user_role || null;
      }
    } catch (error) {
      console.error("Error parsing user data:", error);
    }
    return null;
  });

  const showAlert = (title, message, type = 'info') => {
    setAlertConfig({ title, message, type });
    setShowAlertModal(true);
    if (type !== 'success') {
      setTimeout(() => setShowAlertModal(false), 5000);
    }
  };

  const showConfirm = (title, message, onConfirm, onCancel = null, type = 'warning') => {
    setConfirmConfig({ title, message, onConfirm, onCancel, type });
    setShowConfirmModal(true);
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getAllProjectsafterhomepage();

      if (response.status === 200 && response.data) {
        const transformedProjects = response.data.map(project => ({
          id: `PRJ-${project.project_id.toString().padStart(3, '0')}`,
          projectId: project.project_id,
          name: project.project_name || 'Unnamed Project',
          description: `${project.project_type || 'General Project'} - ${project.project_division || 'No Division'}`,
          startDate: project.cre_usr_dt ? new Date(project.cre_usr_dt).toLocaleDateString('en-GB') : 'N/A',
          endDate: project.last_upd_dt ? new Date(project.last_upd_dt).toLocaleDateString('en-GB') : 'N/A',
          progress: parseProgressFromAPI(project.percentage),
          status: project.isactive ? 'active' : 'hold',
          budget: 'N/A',
          manager: 'N/A',
          location: `${project.land_village || 'N/A'} Villages, ${project.land_tehsil || 'N/A'}`,
          landAcquisitionNo: project.land_acquisition_no,
          landVillage: project.land_village,
          landTehsil: project.land_tehsil,
          landDistrict: project.land_district,
          administrativeOrderNo: project.administrative_order_no,
          administrativeOrderDate: project.administrative_order_date,
          acquisitionProposalNo: project.acquisition_proposal_no,
          acquisitionProposalDate: project.acquisition_proposal_date,
          noOfBudharak: project.no_of_budharak,
          projectInchargeUsrId: project.project_incharge_usr_id,
          stageNo: project.stage_no,
          projectBanner: project.project_banner,
          projectType: project.project_type,
          projectDivision: project.project_division
        }));
        setProjects(transformedProjects);
      } else {
        throw new Error('Failed to fetch projects');
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      setError('Failed to load projects. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const parseProgressFromAPI = (percentage) => {
    if (!percentage) return 0;
    const numericValue = typeof percentage === 'string'
      ? parseFloat(percentage.replace('%', ''))
      : parseFloat(percentage);
    return Math.max(0, Math.min(100, numericValue || 0));
  };

  const handleEditProject = async (project) => {
    try {
      console.log('Editing project:', project);
      const detailResponse = await apiService.getAllProjectsafterhomepage(project.projectId);

      if (detailResponse && detailResponse.status === 200 && detailResponse.data) {
        console.log('Project details fetched:', detailResponse.data);
        const projectData = Array.isArray(detailResponse.data)
          ? detailResponse.data[0]
          : detailResponse.data;
        sessionStorage.setItem('editProjectData', JSON.stringify(projectData));
        setSubComponent('EditProject');
      } else {
        console.log('Using existing project data for editing');
        const fallbackData = {
          project_id: project.projectId,
          project_name: project.name,
          project_type: project.projectType,
          project_division: project.projectDivision,
          land_village: project.landVillage,
          land_tehsil: project.landTehsil,
          land_district: project.landDistrict,
          land_acquisition_no: project.landAcquisitionNo,
          administrative_order_no: project.administrativeOrderNo,
          administrative_order_date: project.administrativeOrderDate,
          acquisition_proposal_no: project.acquisitionProposalNo,
          acquisition_proposal_date: project.acquisitionProposalDate,
          no_of_budharak: project.noOfBudharak,
          project_incharge_usr_id: project.projectInchargeUsrId,
          stage_no: project.stageNo,
          project_banner: project.projectBanner,
          isactive: project.status === 'active'
        };
        sessionStorage.setItem('editProjectData', JSON.stringify(fallbackData));
        setSubComponent('EditProject');
      }
    } catch (error) {
      console.error('Error fetching project details:', error);
      console.log('API failed, using existing project data');
      const fallbackData = {
        project_id: project.projectId,
        project_name: project.name,
        project_type: project.projectType,
        project_division: project.projectDivision,
        land_village: project.landVillage,
        land_tehsil: project.landTehsil,
        land_district: project.landDistrict,
        land_acquisition_no: project.landAcquisitionNo,
        administrative_order_no: project.administrativeOrderNo,
        administrative_order_date: project.administrativeOrderDate,
        acquisition_proposal_no: project.acquisitionProposalNo,
        acquisition_proposal_date: project.acquisitionProposalDate,
        no_of_budharak: project.noOfBudharak,
        project_incharge_usr_id: project.projectInchargeUsrId,
        stage_no: project.stageNo,
        project_banner: project.projectBanner,
        isactive: project.status === 'active'
      };
      sessionStorage.setItem('editProjectData', JSON.stringify(fallbackData));
      setSubComponent('EditProject');
    }
  };

  const handleUpdateProject = async (projectId, updatedData) => {
    try {
      const response = await apiService.updateProject(projectId, updatedData);
      if (response.status === 200) {
        await fetchProjects();
        showAlert('Success', 'Project updated successfully!', 'success');
      }
    } catch (error) {
      console.error('Error updating project:', error);
      showAlert('Error', 'Failed to update project. Please try again.', 'error');
    }
  };

  const filteredProjects = projects.filter(project =>
    project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    project.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    project.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleFilters = () => {
    if (!showFilters) {
      setShowFilters(true);
      setTimeout(() => setFilterAnimation(true), 50);
    } else {
      setFilterAnimation(false);
      setTimeout(() => setShowFilters(false), 300);
    }
  };

  const handleCreateProjectClick = () => {
    setSubComponent('CreateProject');
  };

  const handleRetry = () => {
    fetchProjects();
  };

  const counts = {
    all: projects.length,
    active: projects.filter(p => p.status === 'active').length,
    hold: projects.filter(p => p.status === 'hold').length,
    completed: projects.filter(p => p.status === 'completed').length,
  };

  const columns = [
    { key: 'name', label: translations[language]?.projectName || 'Project Name' },
    { key: 'projectType', label: translations[language]?.projectType || 'Type' },
    { key: 'projectDivision', label: translations[language]?.division || 'Division' },
    { key: 'startDate', label: translations[language]?.startDate || 'Start Date' },
    { key: 'actions', label: translations[language]?.actions.edit || 'Actions' },
  ];

  return (
    <div style={{ fontFamily: "'DM Sans', 'Segoe UI', sans-serif" }}
      className="min-h-[calc(100vh-96px)] bg-slate-50">

      <div className="px-4 sm:px-6 py-5 space-y-4">

        {/* ── ERROR BANNER ── */}
        {error && (
          <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl
                          bg-red-50 border border-red-200 shadow-sm">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-red-500 shrink-0" />
              <span className="text-sm text-red-700">{error}</span>
            </div>
            <button
              onClick={handleRetry}
              className="text-xs font-semibold text-red-600 hover:text-red-800
                         underline underline-offset-2 transition-colors"
            >
              {translations[language]?.retry || 'Retry'}
            </button>
          </div>
        )}

        {/* ── CONTROLS ROW ── */}
        <div className="flex flex-wrap items-center gap-3">

          {/* Search */}
          <div className="relative group">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4
                               text-slate-400 group-focus-within:text-blue-500 transition-colors" />
            <input
              type="text"
              placeholder={translations[language]?.searchProjects || 'Search projects…'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-60 pl-9 pr-8 py-2 text-sm rounded-lg bg-white border border-slate-200
                         text-slate-700 placeholder-slate-400 shadow-sm
                         focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100
                         transition-all duration-200"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2
                           text-slate-400 hover:text-slate-600 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none"
                  viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

    

          {/* Spacer */}
          <div className="flex-1" />

          {/* New Project */}
          {(userRole === ROLES.SAMANVAY || userRole === ROLES.DATA_ENTRY) && (
            <button
              onClick={handleCreateProjectClick}
              className="flex items-center gap-2 px-4 py-2 text-sm font-semibold
                         bg-blue-700 hover:bg-blue-800 text-white rounded-lg
                         shadow-md hover:shadow-lg transition-all duration-200 group"
            >
              <Plus className="h-4 w-4 group-hover:rotate-90 transition-transform duration-300" />
              {translations[language]?.newProject || 'New Project'}
            </button>
          )}
        </div>

        {/* ── TABLE / STATES ── */}
        {loading ? (
          <div className="flex flex-col items-center justify-center h-64 gap-4">
            <div className="relative w-12 h-12">
              <div className="absolute inset-0 rounded-full border-4 border-blue-200" />
              <div className="absolute inset-0 rounded-full border-4 border-t-blue-500
                              border-r-transparent border-b-transparent border-l-transparent animate-spin" />
              <div className="absolute inset-2 flex items-center justify-center">
                <Briefcase className="h-4 w-4 text-blue-400 animate-pulse" />
              </div>
            </div>
            <p className="text-sm text-blue-400">{translations[language]?.loadingProjects || 'Loading projects…'}</p>
          </div>

        ) : filteredProjects.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64">
            <div className="flex flex-col items-center gap-4 bg-white border border-blue-200
                            rounded-2xl shadow-sm px-10 py-10 max-w-sm w-full text-center">
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <FileText className="h-6 w-6 text-blue-400" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-blue-700">
                  {translations[language]?.noProjectsFound || 'No projects found'}
                </h3>
                <p className="text-xs text-blue-400 mt-1">
                  {translations[language]?.adjustSearchCriteria || 'Try adjusting your filters or search.'}
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => { setSearchTerm(''); setView('all'); }}
                  className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-blue-100
                             text-blue-600 hover:bg-blue-200 transition-colors"
                >
                  {translations[language]?.clearFilters || 'Clear filters'}
                </button>
                <button
                  onClick={handleCreateProjectClick}
                  className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-blue-700
                             text-white hover:bg-blue-800 transition-colors"
                >
                  {translations[language]?.addNewProject || 'Add project'}
                </button>
              </div>
            </div>
          </div>

        ) : (
          /* ── SCROLLABLE TABLE ── */
          <div className="w-full overflow-x-auto rounded-xl border border-slate-200 shadow-sm">
            <table className="min-w-full text-sm border-collapse bg-white">

              {/* HEAD */}
              <thead>
                <tr className="bg-blue-700 text-white text-left">
                  {columns.map((col, i) => (
                    <th
                      key={col.key}
                      className={`px-4 py-3 text-[11px] font-semibold uppercase tracking-wider
                                  whitespace-nowrap select-none
                                  ${i === 0 ? 'rounded-tl-xl' : ''}
                                  ${i === columns.length - 1 ? 'rounded-tr-xl text-center' : ''}`}
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>

              {/* BODY */}
              <tbody className="divide-y divide-slate-100">
                {filteredProjects.map((project, index) => (
                  <ProjectRow
                    key={project.id}
                    project={project}
                    index={index}
                    onEdit={handleEditProject}
                    onUpdate={handleUpdateProject}
                    translations={translations}
                    language={language}
                  />
                ))}
              </tbody>

              {/* FOOT – summary row */}
              <tfoot>
                <tr className="bg-slate-50 border-t-2 border-slate-200">
                  <td colSpan={columns.length - 1}
                    className="px-4 py-2.5 text-[11px] text-slate-400 font-medium rounded-bl-xl">
                    Total: {filteredProjects.length} project{filteredProjects.length !== 1 ? 's' : ''}
                  </td>
                  <td className="rounded-br-xl" />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* ── ALERT TOAST ── */}
      {showAlertModal && (
        <div className="fixed top-4 right-4 z-50">
          <div className={`flex items-start gap-3 bg-white rounded-xl shadow-xl border px-4 py-3
                           max-w-xs w-full
                           ${alertConfig.type === 'error' ? 'border-red-200'
              : alertConfig.type === 'warning' ? 'border-amber-200'
                : alertConfig.type === 'info' ? 'border-blue-200'
                  : 'border-emerald-200'}`}>
            <div className={`shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-sm font-bold
                             ${alertConfig.type === 'error' ? 'bg-red-100 text-red-600'
                : alertConfig.type === 'warning' ? 'bg-amber-100 text-amber-600'
                  : alertConfig.type === 'info' ? 'bg-blue-100 text-blue-600'
                    : 'bg-emerald-100 text-emerald-600'}`}>
              {alertConfig.type === 'error' ? '✕'
                : alertConfig.type === 'warning' ? '⚠'
                  : alertConfig.type === 'info' ? 'ℹ' : '✓'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-800">{alertConfig.title}</p>
              <p className="text-xs text-slate-500 mt-0.5">{alertConfig.message}</p>
            </div>
            <button onClick={() => setShowAlertModal(false)}
              className="shrink-0 text-slate-300 hover:text-slate-500 transition-colors">
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* ── CONFIRM MODAL ── */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center
                        justify-center z-50 px-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
            <div className={`h-1 w-full
              ${confirmConfig.type === 'danger' ? 'bg-red-500'
                : confirmConfig.type === 'warning' ? 'bg-amber-400'
                  : 'bg-blue-500'}`} />
            <div className="p-6">
              <div className={`w-11 h-11 rounded-xl mx-auto mb-4 flex items-center justify-center text-xl
                ${confirmConfig.type === 'danger' ? 'bg-red-50 text-red-500'
                  : confirmConfig.type === 'warning' ? 'bg-amber-50 text-amber-500'
                    : 'bg-blue-50 text-blue-500'}`}>
                {confirmConfig.type === 'danger' ? '⚠'
                  : confirmConfig.type === 'warning' ? '⚠' : '?'}
              </div>
              <h3 className="text-base font-bold text-slate-800 text-center mb-1">
                {confirmConfig.title}
              </h3>
              <p className="text-sm text-slate-500 text-center mb-5">
                {confirmConfig.message}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setShowConfirmModal(false);
                    if (confirmConfig.onCancel) confirmConfig.onCancel();
                  }}
                  className="flex-1 py-2 text-sm font-semibold rounded-xl bg-slate-100
                             text-slate-600 hover:bg-slate-200 transition-colors"
                >
                  {translations[language]?.cancel || 'Cancel'}
                </button>
                <button
                  onClick={() => {
                    setShowConfirmModal(false);
                    if (confirmConfig.onConfirm) confirmConfig.onConfirm();
                  }}
                  className={`flex-1 py-2 text-sm font-semibold rounded-xl text-white transition-colors
                    ${confirmConfig.type === 'danger' ? 'bg-red-500 hover:bg-red-600'
                      : confirmConfig.type === 'warning' ? 'bg-amber-500 hover:bg-amber-600'
                        : 'bg-blue-500 hover:bg-blue-600'}`}
                >
                  {translations[language]?.confirm || 'Confirm'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   PROJECT ROW — one <tr> per project, all fields shown
════════════════════════════════════════════════════════ */
const ProjectRow = ({ project, index, onEdit, onUpdate, translations, language }) => {
  const [isHovering, setIsHovering] = useState(false);

  // Status config — same logic, only className strings differ
  const statusConfig = {
    active: {
      badge: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      icon: <Check className="h-3 w-3" />,
      label: translations[language]?.active || 'Active',
    },
    hold: {
      badge: 'bg-amber-50 text-amber-700 border-amber-200',
      icon: <AlertCircle className="h-3 w-3" />,
      label: translations[language]?.onHold || 'On Hold',
    },
    completed: {
      badge: 'bg-blue-50 text-blue-700 border-blue-200',
      icon: <Star className="h-3 w-3" />,
      label: translations[language]?.completed || 'Completed',
    },
  };

  const sc = statusConfig[project.status] || statusConfig.hold;

  const progressColor =
    project.progress > 70 ? 'bg-emerald-400' :
      project.progress > 30 ? 'bg-blue-400' : 'bg-amber-400';

  const progressText =
    project.progress > 70 ? 'text-emerald-600' :
      project.progress > 30 ? 'text-blue-600' : 'text-amber-600';

  const rowBg = index % 2 === 0 ? 'bg-white' : 'bg-slate-50/70';

  return (
    <tr
      className={`${rowBg} hover:bg-blue-50/50 transition-colors duration-150`}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >

      {/* Project Name + description sub-line */}
      <td className="px-4 py-3 min-w-[160px] max-w-[200px]">
        <p className="text-sm font-semibold text-slate-800 leading-snug">
          {project.name}
        </p>
        <p className="text-[11px] text-slate-400 mt-0.5 truncate">
          {project.description}
        </p>
      </td>

      {/* Type */}
      <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-600 font-medium">
        {project.projectType || 'N/A'}
      </td>

      {/* Division */}
      <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-600 font-medium">
        {project.projectDivision || 'N/A'}
      </td>

      {/* Start Date */}
      <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-600 font-medium">
        <div className="flex items-center gap-1.5">
          <Calendar className="h-3 w-3 text-blue-400 shrink-0" />
          {project.startDate}
        </div>
      </td>

      {/* ── ACTIONS — last column ── */}
      <td className="px-4 py-3 whitespace-nowrap text-center">
        <button
          onClick={() => onEdit(project)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold
                     bg-blue-600 hover:bg-blue-700 active:scale-95 text-white
                     rounded-lg shadow-sm transition-all duration-200 group"
        >
          <Edit className="h-3.5 w-3.5 group-hover:scale-110 transition-transform" />
          {translations[language].actions.edit || 'Edit'} 
        </button>
      </td>
    </tr>
  );
};

export default ProjectManagement;